'use client';

// Factiii "F" logo SVG path — matches shared/utils/icons/Logo.tsx
const LOGO_PATH =
  'M56.22 31.73C51.46 31.73 47.8617 32.7783 45.425 34.875C42.9883 36.915 41.77 40.0033 41.77 44.14V47.795H66.25V58.845H41.77V80.52H28V44.225C28 36.575 30.38 30.625 35.14 26.375C39.9567 22.125 46.6717 20 55.285 20C58.685 20 61.8867 20.3683 64.89 21.105C67.8933 21.785 70.5 22.805 72.71 24.165L68.46 35.045C65.06 32.835 60.98 31.73 56.22 31.73Z';
const LOGO_BG = '#6366F1';

const drawLogo = (ctx: CanvasRenderingContext2D, cw: number, ch: number) => {
  const size = Math.max(40, Math.min(cw, ch) * 0.06);
  const pad = size * 0.4;
  const x = cw - size - pad;
  const y = ch - size - pad;
  const r = size * 0.2;

  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = LOGO_BG;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + size - r, y);
  ctx.quadraticCurveTo(x + size, y, x + size, y + r);
  ctx.lineTo(x + size, y + size - r);
  ctx.quadraticCurveTo(x + size, y + size, x + size - r, y + size);
  ctx.lineTo(x + r, y + size);
  ctx.quadraticCurveTo(x, y + size, x, y + size - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();

  const scale = size / 100;
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.fillStyle = 'white';
  ctx.fill(new Path2D(LOGO_PATH));
  ctx.restore();
};

const fetchAsImage = async (url: string): Promise<HTMLImageElement> => {
  const img = new window.Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
  return img;
};

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Download an image from the served VIEW URL.
 * - Watermarked images (Upload.watermarked, a camera factiii photo): the
 *   factiii's Avatar is already baked into the VIEW bytes — save directly.
 * - Every other image, a photo on a factiii without CAMERA included: VIEW
 *   bytes are clean — composite a Logo on top before save.
 */
export const downloadImage = async (
  viewUrl: string,
  watermarked: boolean,
  fileName = 'factiii-image.jpg'
): Promise<void> => {
  if (watermarked) {
    const blob = await (await fetch(viewUrl)).blob();
    triggerDownload(blob, fileName);
    return;
  }

  const img = await fetchAsImage(viewUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(img, 0, 0);
  drawLogo(ctx, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9)
  );
  if (!blob) return;
  triggerDownload(blob, fileName);
};
